import _ from 'lodash';
import classNames from 'classnames';
import moment from 'moment';

import { Typography } from '@vivaahealth/design-system';
import React, { useCallback, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import * as api from '../../../api';
import * as store from '../../../reducers/store';
import * as logger from '../../../logger';

import { getProviderNameFriendly } from '../../../utils/provider';
import { PaymentLinks } from '../../FormEnd/LastPage';
import CalendarEvent from '../../../components/CalendarEvent';
import ProviderImage from '../../../components/ProviderImage';
import NavBar, { View } from '../../../components/NavBar';
import { constructAddress } from '../../../utils/practice';
import { TIME_OF_DAY_FORMAT } from '../../../utils/dateTime';
import { parseValue } from 'common/dist/utils/types';
import { useBookedSlot } from '../../../hooks/schedule/useBookedSlot';

import greenCheckCircle from '../../../assets/green-check-circle.svg';

import styles from './index.module.css';
import formStartStyles from '../../FormStart/index.module.css';

interface StateProps
  extends Pick<
    api.Previsit.PracticePreference,
    'scheduleAppointmentRedirectUrl'
  > {
  formSetId: string | null;
  authenticationCode: string | null;
}

interface Props extends StateProps {}

const getMaybeBookedSlotFromFormSetValue = (
  forms: store.Forms | null,
): api.EhrTimeSlot.TimeSlotConfirmation | null => {
  if (!forms) return null;
  const flattenForms = _.flatten(_.map(forms));
  const formFields = _.flatten(flattenForms.map((form) => form.fields));
  const scheduleWithValue = formFields.find((f) => {
    if (
      f.primitive !==
      api.PrevisitFormTemplateFieldConfig.Primitive.ScheduleAppointment
    ) {
      return false;
    }
    if (!f.value) return false;
    const parsedValue = parseValue<{
      selectedAppointment: api.EhrTimeSlot.TimeSlotConfirmation;
    }>(f.value);
    return !!parsedValue?.selectedAppointment.ehrTimeSlotBookingId;
  });
  if (!scheduleWithValue) return null;
  return (
    parseValue<{
      selectedAppointment: api.EhrTimeSlot.TimeSlotConfirmation;
    }>(scheduleWithValue.value)?.selectedAppointment || null
  );
};

const Confirmation: React.FC<Props> = ({
  formSetId,
  authenticationCode,
  scheduleAppointmentRedirectUrl,
}) => {
  const { search } = useLocation();
  const history = useHistory();
  const bookedSlot = useSelector((store: store.All) => {
    const { inbound, selectedFormSetId, formSets } = store;
    return (
      inbound.bookedSlot ||
      (selectedFormSetId
        ? getMaybeBookedSlotFromFormSetValue(formSets[selectedFormSetId].forms)
        : null)
    );
  }, _.isEqual);
  const { selectedEhrProviderWithPractice, selectedTimeSlot } = useBookedSlot();

  const submitFormSet = useCallback(
    async (formSetId: string, authenticationCode: string) => {
      try {
        await api.client.Previsit.submitFormSet(formSetId, authenticationCode);
      } catch (e) {
        logger.error(e, 'Unable to submit form set.');
      }
    },
    [],
  );

  const updatePage = useCallback(
    (newPage: number) => {
      const queryParams = new URLSearchParams(search.slice(1));
      queryParams.set('page', `${newPage}`);
      history.push({
        pathname: history.location.pathname.replace('/end', ''),
        search: queryParams.toString(),
      });
    },
    [history, search],
  );

  const clearLocalStorage = useCallback(
    async (redirectUrl?: string) => {
      await api.client.Previsit.logout();
      updatePage(1);
      store.resetStore();
      if (redirectUrl) window.location.href = redirectUrl;
    },
    [updatePage],
  );

  useEffect(() => {
    if (!formSetId || !authenticationCode) return;
    submitFormSet(formSetId, authenticationCode);
  }, [formSetId, authenticationCode, submitFormSet]);

  if (!selectedEhrProviderWithPractice || !selectedTimeSlot) {
    return <GeneralFinalPage />;
  }

  if (!bookedSlot) {
    logger.error(new Error('No appointment booked.'));
    clearLocalStorage();
    return <></>;
  }

  const { startTime, endTime } = selectedTimeSlot;
  const { timezone, previsitImageUrl } = selectedEhrProviderWithPractice;
  const fullIdentifier =
    selectedTimeSlot.scheduleAppointmentDisplayBy ===
    api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy.Practice
      ? selectedEhrProviderWithPractice.name
      : getProviderNameFriendly(selectedEhrProviderWithPractice, [
          'firstName',
          'lastName',
          'suffix',
        ]);
  const location = constructAddress(selectedEhrProviderWithPractice);

  return (
    <div className={styles.ConfirmationContainer}>
      <img
        alt="success"
        className={styles.GreenCheckCircle}
        src={greenCheckCircle}
      />
      <h3 className={styles.CenterPageTitle}>Your appointment is confirmed!</h3>
      <div className={styles.Confirmation}>
        {selectedTimeSlot.scheduleAppointmentDisplayBy ===
          api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy
            .Provider && previsitImageUrl ? (
          <ProviderImage
            provider={selectedEhrProviderWithPractice}
            size={'Small'}
          />
        ) : null}
        <div className={styles.ProviderName}>{fullIdentifier}</div>
        <div className={styles.Date}>
          {moment(startTime).tz(timezone).format('MMM Do')} at{' '}
          {moment(startTime).tz(timezone).format(TIME_OF_DAY_FORMAT)}
        </div>
        <div className={styles.Address}>{location}</div>
        {formSetId && (
          <div className={styles.Passbooks}>
            <PaymentLinks formSetId={formSetId} />
          </div>
        )}
        <CalendarEvent
          subject={`Appointment with ${fullIdentifier}`}
          location={location}
          practice={selectedEhrProviderWithPractice}
          provider={
            selectedTimeSlot.scheduleAppointmentDisplayBy ===
            api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy
              .Practice
              ? null
              : selectedEhrProviderWithPractice
          }
          encounter={{
            id: bookedSlot.ehrTimeSlotBookingId,
            startTime,
            endTime,
          }}
        />
      </div>
      <div className={styles.AdditionalAction}>
        <button
          className={styles.TextButton}
          type="button"
          onClick={() =>
            clearLocalStorage(scheduleAppointmentRedirectUrl ?? undefined)
          }
        >
          Schedule another appointment
        </button>
      </div>
    </div>
  );
};

export const ConfirmationPage: React.FC<{}> = () => {
  return (
    <>
      <NavBar view={View.FormStart} />
      <div className={classNames(formStartStyles.FormContainer)}>
        <div className={formStartStyles.Form}>
          <ConfirmationComponent />
        </div>
      </div>
    </>
  );
};

const GeneralFinalPage: React.FC = () => {
  return (
    <>
      <img
        alt="success"
        src={greenCheckCircle}
        className={styles.GreenCheckCircle}
      />
      <Typography variant="headline-large" bold className="mb-3">
        Thank you for your response.
      </Typography>
      <Typography variant="paragraph-large">
        You can now safely exit out of this screen.
      </Typography>
    </>
  );
};

const mapStateToProps = (store: store.All): StateProps => ({
  formSetId: store.selectedFormSetId,
  authenticationCode: store.authenticationCode,
  scheduleAppointmentRedirectUrl:
    store.masterPracticePreference.scheduleAppointmentRedirectUrl,
});

const ConfirmationComponent = connect(mapStateToProps)(Confirmation);

export default ConfirmationComponent;
